/**
 * RoadmapBoard — the visual roadmap surface shared by:
 *   • /roadmap (public passcode-gated page in pages/Roadmap.jsx)
 *   • NEXA → Life → Business → Levanta (nexa/BusinessLevantaDetail.jsx)
 *
 * Both consumers pass an `api(path, opts)` function returning the parsed JSON.
 * The board itself is auth-agnostic — the caller wires headers and role.
 * Because both consumers hit the same MongoDB `roadmap_items` collection,
 * edits from either surface appear on the other automatically.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Compass, Flag, Lightbulb, CalendarClock, Plus, Pencil, Trash2, X,
  CheckCircle2, Circle, Loader2,
} from 'lucide-react';

const STATUS_META = {
  planned:     { label: 'Planned',     chip: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-300' },
  in_progress: { label: 'In progress', chip: 'bg-blue-50 text-blue-700 ring-blue-200',     dot: 'bg-blue-500' },
  done:        { label: 'Done',        chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
};

const GOAL_COLORS = [
  { tag: 'text-blue-600',   bar: 'bg-blue-500',  dot: 'bg-blue-500' },
  { tag: 'text-amber-600',  bar: 'bg-amber-500', dot: 'bg-amber-500' },
  { tag: 'text-teal-600',   bar: 'bg-teal-500',  dot: 'bg-teal-500' },
  { tag: 'text-rose-600',   bar: 'bg-rose-500',  dot: 'bg-rose-500' },
  { tag: 'text-indigo-600', bar: 'bg-indigo-500',dot: 'bg-indigo-500' },
  { tag: 'text-cyan-600',   bar: 'bg-cyan-600',  dot: 'bg-cyan-600' },
];

const SECTIONS = [
  { key: 'milestone', icon: Flag, eyebrow: 'The path', title: 'Milestones',
    blurb: 'One timeline — shipped, in motion, and next up.' },
  { key: 'long_term', icon: Compass, eyebrow: 'The horizon', title: 'Long-term plan',
    blurb: 'Where Levanta is headed over the coming years.' },
];

/* ---------- Edit / create dialog (admin) ---------- */
const EditDialog = ({ item, section, onSave, onClose }) => {
  const [title, setTitle] = useState(item?.title || '');
  const [description, setDescription] = useState(item?.description || '');
  const [targetDate, setTargetDate] = useState(item?.target_date || '');
  const [status, setStatus] = useState(item?.status || 'planned');
  const [isMeeting, setIsMeeting] = useState(item?.kind === 'meeting');
  const [todos, setTodos] = useState(item?.todos ? item.todos.map((t) => ({ ...t })) : []);
  const [steps, setSteps] = useState(item?.steps ? item.steps.map((s) => ({ ...s })) : []);
  const [busy, setBusy] = useState(false);

  const setStepField = (i, f, v) => setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, [f]: v } : s)));
  const toggleStepDone = (i) => setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, done: !s.done } : s)));
  const removeStep = (i) => setSteps((ss) => ss.filter((_, j) => j !== i));
  const addStep = () => setSteps((ss) => [...ss, { id: crypto.randomUUID(), text: '', done: false, eta: '' }]);

  const setTodoText = (i, text) => setTodos((ts) => ts.map((t, j) => (j === i ? { ...t, text } : t)));
  const toggleTodo = (i) => setTodos((ts) => ts.map((t, j) => (j === i ? { ...t, done: !t.done } : t)));
  const removeTodo = (i) => setTodos((ts) => ts.filter((_, j) => j !== i));
  const addTodo = () => setTodos((ts) => [...ts, { id: crypto.randomUUID(), text: '', done: false }]);

  const save = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        target_date: targetDate.trim(),
        status,
        kind: isMeeting ? 'meeting' : 'milestone',
        todos: todos.filter((t) => t.text.trim()).map((t) => ({ ...t, text: t.text.trim() })),
        steps: steps.filter((s) => s.text.trim()).map((s) => ({ ...s, text: s.text.trim(), eta: (s.eta || '').trim() })),
      });
      onClose();
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" data-testid="roadmap-edit-dialog">
      <div className="absolute inset-0 bg-[#0b1f3a]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-full overflow-y-auto rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 p-7">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-black text-[#0b1f3a] tracking-tight">
            {item ? 'Edit item' : 'New item'}
          </h3>
          <button onClick={onClose} data-testid="roadmap-dialog-close" className="p-1.5 rounded-lg hover:bg-black/5 text-[#0b1f3a]/60">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block mt-5 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="roadmap-field-title"
          className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-[#0b1f3a] font-medium" />

        <label className="block mt-4 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="roadmap-field-description"
          className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-[#0b1f3a]/85 text-sm leading-relaxed resize-y" />

        {section !== 'idea' && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Target date</label>
              <input value={targetDate} onChange={(e) => setTargetDate(e.target.value)} placeholder="e.g. Q4 2026" data-testid="roadmap-field-date"
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-[#0b1f3a] text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="roadmap-field-status"
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-[#0b1f3a] text-sm bg-white">
                <option value="planned">Planned</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>
        )}

        {section === 'milestone' && (
          <label className="mt-4 flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={isMeeting} onChange={(e) => setIsMeeting(e.target.checked)} data-testid="roadmap-field-meeting"
              className="w-4 h-4 rounded accent-violet-600" />
            <span className="text-sm font-semibold text-[#0b1f3a]/75 flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-violet-600" /> This is a meeting
            </span>
          </label>
        )}

        {section === 'milestone' && (
          <>
            <div className="mt-5 flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Todos</label>
              <button onClick={addTodo} data-testid="roadmap-add-todo" className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add todo
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {todos.map((t, i) => (
                <div key={t.id} className="flex items-center gap-2">
                  <button onClick={() => toggleTodo(i)} className="shrink-0 text-emerald-600" aria-label="toggle done">
                    {t.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5 text-[#0b1f3a]/25" />}
                  </button>
                  <input value={t.text} onChange={(e) => setTodoText(i, e.target.value)} placeholder="What needs to happen?"
                    data-testid={`roadmap-todo-input-${i}`}
                    className="flex-1 px-3 py-2 rounded-lg ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-sm text-[#0b1f3a]" />
                  <button onClick={() => removeTodo(i)} className="shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-red-500" aria-label="remove todo">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {todos.length === 0 && <p className="text-xs text-[#0b1f3a]/40">No todos yet.</p>}
            </div>
          </>
        )}

        {section === 'long_term' && (
          <>
            <div className="mt-5 flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/50">Path steps</label>
              <button onClick={addStep} data-testid="roadmap-add-step" className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add step
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {steps.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2">
                  <button onClick={() => toggleStepDone(i)} className="shrink-0 text-emerald-600" aria-label="toggle step done">
                    {s.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5 text-[#0b1f3a]/25" />}
                  </button>
                  <input value={s.text} onChange={(e) => setStepField(i, 'text', e.target.value)} placeholder="Step on the path"
                    data-testid={`roadmap-step-input-${i}`}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-sm text-[#0b1f3a]" />
                  <input value={s.eta} onChange={(e) => setStepField(i, 'eta', e.target.value)} placeholder="ETA"
                    data-testid={`roadmap-step-eta-${i}`}
                    className="w-24 shrink-0 px-2.5 py-2 rounded-lg ring-1 ring-black/10 focus:ring-blue-500 focus:outline-none text-sm text-[#0b1f3a]" />
                  <button onClick={() => removeStep(i)} className="shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-red-500" aria-label="remove step">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {steps.length === 0 && <p className="text-xs text-[#0b1f3a]/40">No steps yet — add the plan behind this goal.</p>}
            </div>
          </>
        )}

        <button onClick={save} disabled={!title.trim() || busy} data-testid="roadmap-save-item"
          className="mt-7 w-full px-6 py-3 rounded-xl bg-[#0b1f3a] text-white font-semibold hover:shadow-2xl transition-all duration-200 disabled:opacity-50">
          {busy ? 'Saving…' : item ? 'Save changes' : 'Add to roadmap'}
        </button>
      </div>
    </div>
  );
};

/* ---------- Item card ---------- */
const ItemCard = ({
  item, isAdmin, isMilestone, onEdit, onDelete, onToggleTodo, onToggleStep,
  onToggleStatus, goalStats, goalColor,
}) => {
  const meta = STATUS_META[item.status] || STATUS_META.planned;
  const isMeeting = item.kind === 'meeting';
  const total = item.todos?.length || 0;
  const doneCount = item.todos?.filter((t) => t.done).length || 0;
  const pct = item.status === 'done' ? 100 : total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div data-testid={`roadmap-item-${item.id}`}
      className={`group relative w-full rounded-2xl backdrop-blur shadow-sm hover:shadow-lg transition-shadow duration-200 p-6 ring-1 ${
        isMeeting ? 'bg-violet-50/80 ring-violet-200/80' : 'bg-white/90 ring-black/5'
      }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {!isMilestone && goalColor && (
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${goalColor.dot}`} />
            )}
            <h4 className="text-lg font-bold text-[#0b1f3a] tracking-tight">{item.title}</h4>
            {item.target_date && (
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-600/80">
                {item.target_date}
              </span>
            )}
          </div>
          {item.description && (
            <p className="mt-1.5 text-sm text-[#0b1f3a]/65 leading-relaxed">{item.description}</p>
          )}
        </div>
        {isMeeting ? (
          <span className="shrink-0 flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ring-1 bg-violet-100 text-violet-700 ring-violet-300"
                data-testid={`roadmap-meeting-badge-${item.id}`}>
            <CalendarClock className="w-3.5 h-3.5" /> Meeting{item.status === 'done' ? ' ✓' : ''}
          </span>
        ) : (
          <span className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full ring-1 ${meta.chip}`}>
            {meta.label}
          </span>
        )}
      </div>

      {goalStats && goalStats.total > 0 && (
        <div className="mt-4" data-testid={`roadmap-goal-progress-${item.id}`}>
          <div className="flex items-center justify-between text-[11px] font-semibold">
            <span className="uppercase tracking-[0.14em] text-[#0b1f3a]/45">Progress</span>
            <span className="font-mono text-[#0b1f3a]/55">{goalStats.done}/{goalStats.total} steps · {goalStats.pct}%</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-black/5 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${goalColor ? goalColor.bar : 'bg-blue-500'}`}
                 style={{ width: `${goalStats.pct}%` }} />
          </div>
        </div>
      )}

      {item.steps?.length > 0 && (
        <div className="mt-4 border-t border-black/5 pt-3" data-testid={`roadmap-steps-${item.id}`}>
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#0b1f3a]/45">The path</p>
          <ul className="mt-2 space-y-1.5">
            {item.steps.map((s) => (
              <li key={s.id} className="flex items-start gap-2 text-sm">
                <button onClick={isAdmin ? () => onToggleStep(item, s) : undefined}
                        data-testid={`roadmap-step-toggle-${s.id}`}
                        className={`mt-0.5 shrink-0 ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                        aria-label={s.done ? 'mark step not done' : 'mark step done'}>
                  {s.done
                    ? <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500" />
                    : <Circle className="w-[18px] h-[18px] text-[#0b1f3a]/25" />}
                </button>
                <span className={`min-w-0 ${s.done ? 'text-[#0b1f3a]/40 line-through' : 'text-[#0b1f3a]/80'}`}>
                  {s.text}
                </span>
                {!s.done && s.eta && (
                  <span className={`ml-auto shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] ${goalColor ? goalColor.tag : 'text-blue-600'}`}>
                    {s.eta}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(total > 0 || item.status !== 'planned') && isMilestone && !isMeeting && (
        <div className="mt-4 h-1.5 rounded-full bg-black/5 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${item.status === 'done' ? 'bg-emerald-500' : 'bg-blue-500'}`}
               style={{ width: `${pct}%` }} />
        </div>
      )}

      {total > 0 && (
        <ul className="mt-4 space-y-1.5">
          {item.todos.map((t) => (
            <li key={t.id} className="flex items-start gap-2 text-sm">
              <button onClick={isAdmin ? () => onToggleTodo(item, t) : undefined}
                      data-testid={`roadmap-todo-toggle-${t.id}`}
                      className={`mt-0.5 shrink-0 ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                      aria-label={t.done ? 'mark not done' : 'mark done'}>
                {t.done
                  ? <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500" />
                  : <Circle className="w-[18px] h-[18px] text-[#0b1f3a]/25" />}
              </button>
              <span className={t.done ? 'text-[#0b1f3a]/40 line-through' : 'text-[#0b1f3a]/80'}>
                {t.text}
              </span>
            </li>
          ))}
        </ul>
      )}

      {isAdmin && (
        <div className="absolute top-4 right-4 hidden group-hover:flex items-center gap-1 bg-white rounded-xl shadow-lg ring-1 ring-black/5 p-1">
          {onToggleStatus && (isMilestone || item.section === 'milestone') && (
            <button onClick={() => onToggleStatus(item)} data-testid={`roadmap-toggle-status-${item.id}`}
                    className={`p-2 rounded-lg ${item.status === 'done' ? 'text-emerald-600 hover:bg-emerald-50' : 'text-[#0b1f3a]/60 hover:bg-emerald-50 hover:text-emerald-600'}`}
                    aria-label={item.status === 'done' ? 'Reopen' : 'Mark done'}
                    title={item.status === 'done' ? 'Reopen' : 'Mark done'}>
              {item.status === 'done'
                ? <CheckCircle2 className="w-4 h-4" />
                : <Circle className="w-4 h-4" />}
            </button>
          )}
          <button onClick={() => onEdit(item)} data-testid={`roadmap-edit-${item.id}`}
                  className="p-2 rounded-lg hover:bg-blue-50 text-blue-600" aria-label="Edit">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(item)} data-testid={`roadmap-delete-${item.id}`}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------- Board (shared) ---------- */
/**
 * Props:
 *   api            → async (path, opts?) => JSON, path is under the roadmap namespace
 *                    ("/items", "/items/{id}"). Errors should have `.status` when 401.
 *   isAdmin        → whether edit controls render (defaults false).
 *   onAuthLost     → optional: called if a request returns 401.
 *   headerSlot     → optional React node rendered in the top-right area of hero.
 *   heroEyebrow    → small pre-title eyebrow text (default "Unlisted · Internal").
 *   heroTitle      → main title (default "The Levanta roadmap").
 *   heroBlurb      → sub-title paragraph.
 *   className      → optional wrapper class overrides.
 */
export const RoadmapBoard = ({
  api,
  isAdmin = false,
  onAuthLost,
  headerSlot = null,
  heroEyebrow = 'Unlisted · Internal',
  heroTitle = 'The Levanta roadmap',
  heroBlurb,
  className = '',
}) => {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api('/items');
      setItems(data.items);
    } catch (e) {
      if (e.status === 401 && onAuthLost) onAuthLost();
    }
  }, [api, onAuthLost]);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (payload) => {
    if (dialog.item) {
      const updated = await api(`/items/${dialog.item.id}`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
    } else {
      const created = await api('/items', {
        method: 'POST',
        body: JSON.stringify({ ...payload, section: dialog.section }),
      });
      setItems((list) => [...list, created]);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    await api(`/items/${item.id}`, { method: 'DELETE' });
    setItems((list) => list.filter((i) => i.id !== item.id));
  };

  const toggleTodo = async (item, todo) => {
    const todos = item.todos.map((t) => (t.id === todo.id ? { ...t, done: !t.done } : t));
    let status = item.status;
    if (todos.length > 0) {
      const allDone = todos.every((t) => t.done);
      if (allDone && status !== 'done') status = 'done';
      else if (!allDone && status === 'done') status = 'planned';
    }
    const payload = status !== item.status ? { todos, status } : { todos };
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, ...payload } : i)));
    try {
      const updated = await api(`/items/${item.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
    } catch { load(); }
  };

  const toggleItemStatus = async (item) => {
    const status = item.status === 'done' ? 'planned' : 'done';
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, status } : i)));
    try {
      const updated = await api(`/items/${item.id}`, { method: 'PUT', body: JSON.stringify({ status }) });
      setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
    } catch { load(); }
  };

  const toggleStep = async (item, step) => {
    const steps = item.steps.map((s) => (s.id === step.id ? { ...s, done: !s.done } : s));
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, steps } : i)));
    try {
      const updated = await api(`/items/${item.id}`, { method: 'PUT', body: JSON.stringify({ steps }) });
      setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
    } catch { load(); }
  };

  const milestones = (items || []).filter((i) => i.section === 'milestone' && i.kind !== 'meeting');
  const overallDone = milestones.filter((m) => m.status === 'done').length;
  const overallPct = milestones.length ? Math.round((overallDone / milestones.length) * 100) : 0;
  const goals = (items || []).filter((i) => i.section === 'long_term');
  const goalColorById = Object.fromEntries(
    goals.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((g, i) => [g.id, GOAL_COLORS[i % GOAL_COLORS.length]])
  );
  const goalStatsById = Object.fromEntries(goals.map((g) => {
    const steps = g.steps || [];
    const done = steps.filter((s) => s.done).length;
    return [g.id, { done, total: steps.length, pct: steps.length ? Math.round((done / steps.length) * 100) : 0 }];
  }));

  return (
    <div className={className} data-testid="roadmap-board">
      {/* Hero */}
      <section className="pt-2 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold">
              {heroEyebrow}
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-[#0b1f3a]">
              {heroTitle}
            </h1>
            {heroBlurb && (
              <p className="mt-4 max-w-2xl text-[#0b1f3a]/65 leading-relaxed">
                {heroBlurb}{isAdmin ? ' You are in edit mode — changes save instantly.' : ''}
              </p>
            )}
          </div>
          {headerSlot}
        </div>
      </section>

      {items === null ? (
        <div className="py-24 flex justify-center text-blue-600">
          <Loader2 className="w-7 h-7 animate-spin" />
        </div>
      ) : (
        <>
          {SECTIONS.map(({ key, icon: Icon, eyebrow, title, blurb }) => {
            const sectionItems = items
              .filter((i) => i.section === key)
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            return (
              <section key={key} className="mt-14" data-testid={`roadmap-section-${key}`}>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold flex items-center gap-2">
                      <Icon className="w-4 h-4" /> {eyebrow}
                    </p>
                    <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight text-[#0b1f3a]">{title}</h2>
                    <p className="mt-1 text-sm text-[#0b1f3a]/55">{blurb}</p>
                  </div>
                  {isAdmin && (
                    <button onClick={() => setDialog({ section: key, item: null })}
                            data-testid={`roadmap-add-${key}`}
                            className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0b1f3a] text-white text-sm font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  )}
                </div>

                {key === 'milestone' ? (
                  <>
                    {milestones.length > 0 && (
                      <div className="mt-6 rounded-2xl bg-white/90 ring-1 ring-black/5 shadow-sm p-5" data-testid="roadmap-overall-progress">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-[#0b1f3a]">Milestone progress</span>
                          <span className="font-mono text-[#0b1f3a]/60">{overallDone}/{milestones.length} · {overallPct}%</span>
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-black/5 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700" style={{ width: `${overallPct}%` }} />
                        </div>
                      </div>
                    )}
                    <div className="mt-7 relative pl-6 sm:pl-8">
                      <div className="absolute left-[9px] sm:left-[13px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-200 via-blue-300 to-transparent" />
                      <div className="space-y-5">
                        {sectionItems.map((item) => (
                          <div key={item.id} className="relative">
                            <span className={`absolute -left-6 sm:-left-8 top-6 w-[19px] h-[19px] sm:w-[27px] sm:h-[27px] flex items-center justify-center`}>
                              <span className={`w-3 h-3 rounded-full ring-4 ring-white shadow ${item.kind === 'meeting' ? 'bg-violet-500' : (STATUS_META[item.status] || STATUS_META.planned).dot}`} />
                            </span>
                            <ItemCard item={item} isAdmin={isAdmin} isMilestone
                              onEdit={(i) => setDialog({ section: key, item: i })}
                              onDelete={deleteItem}
                              onToggleTodo={toggleTodo}
                              onToggleStep={toggleStep}
                              onToggleStatus={toggleItemStatus} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div data-testid="roadmap-longterm-scroller"
                         className="mt-7 flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-5 px-5 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 sm:overflow-visible">
                      {sectionItems.map((item) => (
                        <div key={item.id} className="snap-center shrink-0 w-[82%] max-w-sm sm:w-auto sm:max-w-none sm:shrink flex">
                          <ItemCard item={item} isAdmin={isAdmin} isMilestone={false}
                            goalStats={goalStatsById[item.id]}
                            goalColor={goalColorById[item.id]}
                            onEdit={(i) => setDialog({ section: key, item: i })}
                            onDelete={deleteItem}
                            onToggleTodo={toggleTodo}
                            onToggleStep={toggleStep}
                            onToggleStatus={toggleItemStatus} />
                        </div>
                      ))}
                    </div>
                    {sectionItems.length > 1 && (
                      <p className="mt-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0b1f3a]/35 sm:hidden">
                        Swipe →
                      </p>
                    )}
                  </>
                )}
                {sectionItems.length === 0 && (
                  <p className="mt-6 text-sm text-[#0b1f3a]/40 italic">Nothing here yet.</p>
                )}
              </section>
            );
          })}

          {/* Idea box */}
          <section className="mt-14" data-testid="roadmap-section-idea">
            <div className="rounded-3xl border-2 border-dashed border-blue-300/60 bg-white/50 p-6 sm:p-8">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" /> The chaos box
                  </p>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight text-[#0b1f3a]">Future ideas</h2>
                  <p className="mt-1 text-sm text-[#0b1f3a]/55">
                    Things — and not-yet-things — that may happen someday. Not scheduled, not promised.
                  </p>
                </div>
                {isAdmin && (
                  <button onClick={() => setDialog({ section: 'idea', item: null })}
                          data-testid="roadmap-add-idea"
                          className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0b1f3a] text-white text-sm font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                )}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {items
                  .filter((i) => i.section === 'idea')
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((item) => (
                    <div key={item.id} data-testid={`roadmap-item-${item.id}`} title={item.description || undefined}
                         className="group flex items-center gap-2 rounded-full bg-white ring-1 ring-black/10 shadow-sm px-4 py-2 text-sm font-semibold text-[#0b1f3a]/80">
                      {item.title}
                      {isAdmin && (
                        <span className="flex items-center gap-0.5 -mr-1">
                          <button onClick={() => setDialog({ section: 'idea', item })}
                                  data-testid={`roadmap-edit-${item.id}`}
                                  className="p-1 rounded-full hover:bg-blue-50 text-blue-600" aria-label="Edit idea">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => deleteItem(item)}
                                  data-testid={`roadmap-delete-${item.id}`}
                                  className="p-1 rounded-full hover:bg-red-50 text-red-500" aria-label="Delete idea">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                {items.filter((i) => i.section === 'idea').length === 0 && (
                  <p className="text-sm text-[#0b1f3a]/40 italic">The box is empty — for now.</p>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {dialog && (
        <EditDialog item={dialog.item} section={dialog.section}
          onSave={saveItem} onClose={() => setDialog(null)} />
      )}
    </div>
  );
};

export default RoadmapBoard;
